/**
 * Asset Swap Service
 *
 * Replaces an asset instance with a new one of a different type while
 * preserving the timeline row's visual position.
 *
 * Renderer:   normal assetInstanceAdded (new instanceId) +
 *             assetInstanceRemoved (old instanceId).
 * Timeline:   old Redux entry is replaced at the same array index so
 *             the row never flickers or jumps.
 * DB:         old row is deleted; new row is created — two separate rows.
 * Keyframes & compatible clips are copied to the new instanceId.
 */

import { AssetInstance, SupabaseAssetInstance } from '@/types/assetTypes';
import { BaseAsset } from '@/modules/library/types';
import { AssetServiceImpl } from '@/modules/databaseService/assets';
import { KeyframeServiceImpl } from '@/modules/keyframes/KeyframeService';
import { ClipServiceImpl } from '@/modules/databaseService/clips';
import type { RendererBridge } from '@/modules/rendererBridge';
import { LibraryDataProvider } from '@/modules/library/types';
import { Services } from '@/infrastructure/services';
import { addSceneAsset, removeSceneAssets, replaceSceneAsset, setSelectedAsset } from '@/features/sceneAssets/sceneAssetsActions';
import { loadKeyframesForAsset } from '@/features/keyframes/keyframesThunks';
import { upsertKeyframe, clearKeyframesForAsset, selectKeyframesByAssetId } from '@/features/keyframes/keyframesSlice';
import { upsertClip, clearClipsForAsset } from '@/features/clips/clipsSlice';
import { convertToAssetInstance, convertToClipInstance } from '@/utils/dataConverters';
import { safeParseJson } from '@/utils/safeParseJson';
import { sendEditorKeyframeSelected } from '@/pages/editor/components/timeline/TimelineCategoryItem/keyframeSelectionHelpers';
import { formatVector3String } from '@/utils/numberUtils';
import { removeAssetFromScene } from '@/utils/handleRemoveAsset';
import { SupabaseClipInstance } from '@/types/clipTypes';
import { Uid } from '@/utils/utils';
import { Keyframe, SupabaseKeyframe } from '@/modules/keyframes/types';
import { globalLibraryCache } from '@/modules/library/libraryCache';
import {
  IAssetSwapService,
  SwapValidationResult,
  SwapResult,
  SwapParams,
  SwapContext,
} from './types';
import { swapModeRegistry } from './swapModeRegistry';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { assetTypeToLegacy } from '@/infrastructure/api/v1/mappers/assetTypes';

import { abandoned, rolledBack, type Gesture } from '@/modules/undoRedo/UndoStack';
import { poseStated } from '@/modules/keyframes/localEdits';

/** A swap that did not finish, carrying its result through the gesture so the gesture rolls back. */
class SwapFailed extends Error {
  constructor(readonly result: SwapResult) {
    super('the swap did not finish');
  }
}

export class AssetSwapService implements IAssetSwapService {
  constructor(
    private readonly assetService: AssetServiceImpl,
    private readonly keyframeService: KeyframeServiceImpl,
    private readonly clipService: ClipServiceImpl,
    private readonly bridge: RendererBridge,
    private readonly libraryService: LibraryDataProvider,
    private readonly services: Services
  ) {}

  /**
   * Validates whether an asset can be swapped to a new type
   */
  validateSwap(originalAsset: AssetInstance, newAsset: BaseAsset): SwapValidationResult {
    const isOriginalAnchor =
      originalAsset.instanceCategory === 'immersal' ||
      originalAsset.instanceCategory === 'image_anchor' ||
      originalAsset.instanceCategory === 'geospatial_anchor';
    const isNewAnchor =
      newAsset.type === 'immersal' ||
      newAsset.type === 'image_anchor' ||
      newAsset.type === 'geospatial_anchor';

    // Rule 1: Cannot swap to the same asset type
    // Exception: anchor-to-anchor swaps should still be allowed even when typeId
    // matches, so we can perform explicit delete + add (same behavior as upload swap).
    if (originalAsset.typeId === newAsset.typeId && !(isOriginalAnchor && isNewAnchor)) {
      return { canSwap: false, reason: 'Cannot swap to the same asset' };
    }
    
    // Rule 2: Cannot swap camera assets (they're special system assets)
    if (originalAsset.instanceCategory === 'camera') {
      return { canSwap: false, reason: 'Camera assets cannot be swapped' };
    }
    
    // No other restrictions per design decision
    return { canSwap: true };
  }

  /**
   * Finds an asset by ID using multiple fallback strategies
   */
  async findAssetForSwap(assetId: string, context: SwapContext): Promise<BaseAsset | null> {
    if (!assetId || assetId === 'upload' || assetId === 'dots') return null;

    console.log('[AssetSwapService] Finding asset for swap:', assetId, 'context:', context);

    try {
      let asset: BaseAsset | null = null;

      // For special asset types (immersal, image_anchor), search in their specific category first
      if (context.isSpecial && context.assetType) {
        const isAnchorSwap =
          context.assetType === 'immersal' ||
          context.assetType === 'image_anchor' ||
          context.assetType === 'geospatial_anchor';
        const specialCategory = isAnchorSwap ? 'anchor' : context.assetType;
        console.log('[AssetSwapService] Searching in special category:', specialCategory);
        const specialAssets = await this.libraryService.fetchMyAssetsByCategory(
          specialCategory,
          undefined,
          context.sceneId,
          context.userId
        );
        console.log('[AssetSwapService] Found special assets:', specialAssets.length);
        asset = specialAssets.find(a => a.typeId === assetId) || null;
      }

      // Try fetching from my assets
      if (!asset && context.userId) {
        const myAssets = await this.libraryService.fetchMyAssets(context.userId);
        console.log('[AssetSwapService] Found my assets:', myAssets.length);
        asset = myAssets.find(a => a.typeId === assetId) || null;
      }

      // If not found, try all assets by category (for the active tab's category)
      if (!asset) {
        // Determine which category to search based on active tab or current asset type
        const searchCategory = context.isSpecial 
          ? context.assetType 
          : (context.activeTab === 'myLibrary' ? undefined : context.activeTab);
        
        if (searchCategory) {
          console.log('[AssetSwapService] Searching all assets by category:', searchCategory);
          const categoryAssets = await this.libraryService.fetchAllAssetsByCategory(
            searchCategory === '3d' ? '3d' : searchCategory,
            context.userId
          );
          console.log('[AssetSwapService] Found category assets:', categoryAssets.length);
          asset = categoryAssets.find(a => a.typeId === assetId) || null;
        }
      }

      // If still not found, try project assets
      if (!asset && context.sceneId) {
        const sceneResponse = await toResult(
          v1.GET('/api/v1/scenes/{sceneUniqueId}', { params: { path: { sceneUniqueId: context.sceneId } } }),
        );

        if (!sceneResponse.error && sceneResponse.data) {
          const projectAssets = await this.libraryService.fetchProjectAssets(
            sceneResponse.data.projectUniqueId,
            context.sceneId
          );
          const allProjectAssets = [...projectAssets.sceneAssets, ...projectAssets.projectAssets];
          console.log('[AssetSwapService] Found project assets:', allProjectAssets.length);
          asset = allProjectAssets.find(a => a.typeId === assetId) || null;
        }
      }

      // Last resort: query the asset_types table directly for anchor/special assets
      if (!asset && context.isSpecial) {
        console.log('[AssetSwapService] Last resort: querying asset_types directly');
        const { data: dto, error } = await toResult(
          v1.GET('/api/v1/asset-types/{assetTypeUniqueId}', { params: { path: { assetTypeUniqueId: assetId } } }),
        );

        if (!error && dto) {
          const data = assetTypeToLegacy(dto);
          asset = {
            typeId: data.asset_type_id,
            typeDisplayName: data.asset_type_display_name,
            typeImgUrl: data.asset_type_img_url,
            type: data.asset_type_category,
            createdAt: data.created_at,
            userId: data.user_id
          };
          console.log('[AssetSwapService] Found asset directly:', asset);
        }
      }

      if (!asset) {
        console.error('[AssetSwapService] Asset not found for swap:', assetId);
      }

      return asset;
    } catch (error) {
      console.error('[AssetSwapService] Error finding asset for swap:', error);
      return null;
    }
  }

  /**
   * Execute the asset swap operation, as one undo step, all or nothing: a swap that does not finish is rolled
   * back (the replacement deleted, the original put back), inside `within` too, which gets the failed result.
   * A rollback that cannot finish leaves `newAssetInstanceId` set while the replacement is saved, and the
   * editor shows what is saved. One abandoned because another scene opened leaves the editor be.
   */
  async executeSwap(params: SwapParams, within?: Gesture): Promise<SwapResult> {
    try {
      return await this.services.undo.transaction('Swap asset', async gesture => {
        const result = await this.swap(params, gesture);
        if (!result.success) throw new SwapFailed(result);
        return result;
      }, within, { rollBackOnError: true });
    } catch (error) {
      if (!(error instanceof SwapFailed)) throw error;
      // Rolled back: the replacement is gone
      if (rolledBack(error)) error.result.newAssetInstanceId = undefined;
      else if (!abandoned(error) && !(await this.showWhatIsSaved(params, error.result.newAssetInstanceId))) {
        error.result.newAssetInstanceId = undefined;
      }
      return error.result;
    }
  }

  /**
   * After a rollback that could not finish, the original and the replacement are each in the editor exactly
   * when they are saved (the renderer already follows what is saved), and the user is told. Resolves whether
   * the replacement is still saved (or could not be read, so may be).
   */
  private async showWhatIsSaved(params: SwapParams, replacementId: string | undefined): Promise<boolean> {
    let replacementSaved = !!replacementId;
    const ids = [params.originalAssetId, replacementId].filter((id): id is string => !!id);
    for (const id of ids) {
      const rows = await this.assetService.fetchAssetInstanceRow(id).catch(() => null);
      if (!rows) continue;
      const saved = rows[0] as SupabaseAssetInstance | undefined;
      if (id === replacementId) replacementSaved = !!saved;
      const live = params.getState().sceneAssets.sceneAssetsArray as AssetInstance[];
      const shown = live.some(a => a.instanceId === id);
      if (saved && !shown) {
        const besideId = id === params.originalAssetId ? replacementId : params.originalAssetId;
        const at = live.findIndex(a => a.instanceId === besideId);
        params.dispatch(addSceneAsset(convertToAssetInstance(saved), at < 0 ? undefined : at + 1));
        await params.dispatch(loadKeyframesForAsset(this.services, id)).catch(() => undefined);
        const clips = await this.clipService.fetchAllSceneClips(params.sceneId).catch(() => []);
        for (const clip of clips.filter((c: SupabaseClipInstance) => c.asset_instance_id === id)) {
          params.dispatch(upsertClip(convertToClipInstance(clip)));
        }
      } else if (!saved && shown) {
        params.dispatch(removeSceneAssets([id]));
        params.dispatch(clearKeyframesForAsset(id));
        params.dispatch(clearClipsForAsset(id));
      }
    }
    const { toast } = await import('sonner');
    toast.error('The swap failed and could not be fully undone. The timeline shows what is saved; check the scene.');
    return replacementSaved;
  }

  private async swap(params: SwapParams, gesture: Gesture): Promise<SwapResult> {
    const result: SwapResult = {
      success: false,
      keyframesCopied: 0,
      clipsCopied: 0,
      warnings: []
    };

    try {
      console.log('🔄 [AssetSwapService] Starting swap operation', { 
        originalAssetId: params.originalAssetId, 
        newAssetType: params.newAsset.typeDisplayName 
      });
      
      // A location anchor (immersal, image or geospatial) is swapped simply
      if (this.requiresSimpleSwap(params.originalCategory) || this.requiresSimpleSwap(params.newAsset.type ?? null)) {
        console.log('🔄 [AssetSwapService] Detected location anchor - using simple swap');
        return this.executeSimpleSwap(params, gesture);
      }

      // Full swap with keyframes and clips
      return this.executeFullSwap(params, gesture);
      
    } catch (error) {
      console.error('❌ [AssetSwapService] Swap failed:', error);
      result.warnings.push((error as Error).message);
      return result;
    }
  }

  /**
   * Check if an asset type requires simple swap (no keyframe/clip copying)
   */
  private requiresSimpleSwap(assetCategory: string | null): boolean {
    const simpleSwapCategories = ['immersal', 'image_anchor', 'geospatial_anchor'];
    return simpleSwapCategories.includes(assetCategory || '');
  }

  /**
   * Execute a simple swap for location anchor assets (immersal scans, image anchors).
   * These don't have keyframes or clips to preserve - just delete and add.
   */
  private async executeSimpleSwap(params: SwapParams, gesture: Gesture): Promise<SwapResult> {
    const result: SwapResult = {
      success: false,
      keyframesCopied: 0,
      clipsCopied: 0,
      warnings: []
    };

    try {
      console.log('🔄 [AssetSwapService] Starting simple swap (location anchor)', { 
        originalAssetId: params.originalAssetId, 
        newAssetType: params.newAsset.typeDisplayName 
      });

      // STEP 1: Remove Original Asset, unless the upload of its replacement removed it already
      if (!swapModeRegistry.hasPreSwapDeleted()) {
        await removeAssetFromScene(
          params.originalAssetId,
          params.sceneId,
          params.dispatch,
          this.services,
          params.getState,
          // Awaited so the delete is part of the swap's undo step and done before the new anchor is added and synced
          { clearCache: true, awaitDbCleanup: true, scope: gesture }
        );
      }
      
      console.log('✅ [AssetSwapService] Removed original location anchor');

      // STEP 2: Add New Asset to Scene
      const newAssetInstanceData = await this.assetService.addAssetToScene(
        {
          typeId: params.newAsset.typeId,
          typeDisplayName: params.newAsset.typeDisplayName,
          type: params.newAsset.type || undefined
        },
        params.userId,
        params.sceneId,
        { keyframes_on: true }, // Need keyframes for Unity to register and make selectable
        undefined,
        undefined,
        undefined,
        undefined,
        gesture
      );
      
      if (!newAssetInstanceData) {
        throw new Error('Failed to create new asset instance');
      }
      
      result.newAssetInstanceId = newAssetInstanceData.asset_instance_id;
      console.log('✅ [AssetSwapService] Created new location anchor', { 
        newAssetInstanceId: newAssetInstanceData.asset_instance_id 
      });

      // STEP 3: Load Keyframes into Redux
      try {
        await params.dispatch(
          loadKeyframesForAsset(
            this.services,
            newAssetInstanceData.asset_instance_id
          )
        );
        console.log('✅ [AssetSwapService] Loaded keyframes into Redux');
      } catch (e) {
        console.warn('[AssetSwapService] Failed to load keyframes into Redux:', e);
        result.warnings.push('Failed to load keyframes - asset may not be selectable');
      }

      // STEP 4: Update Redux with New Asset
      params.dispatch(addSceneAsset(convertToAssetInstance(newAssetInstanceData)));

      // STEP 5: Update Selection
      params.dispatch(setSelectedAsset(newAssetInstanceData.asset_instance_id));

      // Clear library cache
      const cacheKey = `${params.sceneId}_defaultAssets`;
      if (globalLibraryCache[cacheKey]) {
        delete globalLibraryCache[cacheKey];
      }

      result.success = true;
      console.log('🎉 [AssetSwapService] Simple swap completed successfully', {
        newAssetInstanceId: result.newAssetInstanceId
      });
      
      return result;
      
    } catch (error) {
      console.error('❌ [AssetSwapService] Simple swap failed:', error);
      result.warnings.push((error as Error).message);
      return result;
    }
  }

  /**
   * Execute full swap with keyframes and clips preservation.
   *
   * Unity flow: normal create-new / remove-old (new instanceId each time).
   * Timeline visual: old row replaced in-place in Redux immediately after the
   * new instance is created — no flicker, no delay.
   */
  private async executeFullSwap(params: SwapParams, gesture: Gesture): Promise<SwapResult> {
    const result: SwapResult = {
      success: false,
      newAssetInstanceId: '',
      keyframesCopied: 0,
      clipsCopied: 0,
      warnings: [],
    };

    try {
      // STEP 1: The lifespan, flags and keyframes as the editor holds them, with any edit still saving; only the clips are read
      const originalAsset = (params.getState().sceneAssets.sceneAssetsArray as AssetInstance[])
        .find(a => a.instanceId === params.originalAssetId);
      if (!originalAsset) throw new Error('Original asset not found');
      if (selectKeyframesByAssetId(params.getState(), params.originalAssetId).length === 0) {
        await params.dispatch(loadKeyframesForAsset(this.services, params.originalAssetId));
      }
      const originalKeyframes = selectKeyframesByAssetId(params.getState(), params.originalAssetId);
      const [allSceneClips, newAssetTypeRow] = await Promise.all([
        this.clipService.fetchAllSceneClips(params.sceneId),
        this.assetService.fetchAssetTypeRow(params.newAsset.typeId),
      ]);
      const originalStartKeyframe = originalKeyframes.find(kf => kf.category === 'start');
      const originalClips = allSceneClips.filter(
        (c: SupabaseClipInstance) => c.asset_instance_id === params.originalAssetId
      );

      const newAssetType = Array.isArray(newAssetTypeRow) ? newAssetTypeRow[0] : newAssetTypeRow;
      const newCategory: string = newAssetType?.asset_type_category ?? originalAsset.instanceCategory ?? '';
      // Swapped into an anchor, nothing of the original's pose or keyframes carries over
      if (this.requiresSimpleSwap(newCategory)) return this.executeSimpleSwap(params, gesture);
      const singularKey = newCategory;
      const pluralKey =
        newCategory === 'prop' ? 'props' :
        newCategory === 'character' ? 'characters' :
        newCategory === 'mask' ? 'masks' :
        newCategory === 'audio' ? 'audio' :
        newCategory === 'image' ? 'props' : newCategory;

      // STEP 2: Create new asset instance (sends AssetInstanceAdded to Unity)
      const startPose = originalStartKeyframe && {
        position: originalStartKeyframe.position ?? { x: 0, y: 0, z: 0 },
        rotation: originalStartKeyframe.rotation ?? { x: 0, y: 0, z: 0 },
        scale: originalStartKeyframe.scale ?? { x: 1, y: 1, z: 1 },
      };
      const instanceOverrides: Partial<SupabaseAssetInstance> = {
        asset_instance_begin_time:    originalAsset.instanceBeginTime,
        asset_instance_end_time:      originalAsset.instanceEndTime,
        asset_instance_display_order: originalAsset.instanceDisplayOrder,
        asset_instance_display_name:  params.newAsset.typeDisplayName,
        // Billboarding belongs to the placement, not the model, so it survives the swap
        asset_instance_is_billboarded: originalAsset.isBillboarded ?? false,
        ...(startPose
          ? {
              asset_instance_position: formatVector3String(startPose.position, 3),
              asset_instance_rotation: formatVector3String(startPose.rotation, 3),
              asset_instance_scale:    formatVector3String(startPose.scale, 3),
            }
          : {}),
      };

      const newAssetInstanceData = await this.assetService.addAssetToScene(
        { typeId: params.newAsset.typeId, typeDisplayName: params.newAsset.typeDisplayName, type: params.newAsset.type || undefined },
        params.userId,
        params.sceneId,
        { keyframes_on: true },
        undefined,
        newAssetType ?? undefined,
        instanceOverrides,
        undefined,
        gesture
      );

      if (!newAssetInstanceData) throw new Error('Failed to create new asset instance');
      result.newAssetInstanceId = newAssetInstanceData.asset_instance_id;

      // Hidden and locked stay with the placement, set once it is added: the renderer shows an add visible and unlocked
      const hidden = originalAsset.isVisible === false;
      const locked = !!originalAsset.isLocked;
      if (hidden) await this.services.asset.updateAssetInstanceActiveState(newAssetInstanceData.asset_instance_id, false, gesture);
      if (locked) await this.services.asset.updateAssetInstanceLockState(newAssetInstanceData.asset_instance_id, true, gesture);

      // STEP 3: Redux update — replace old entry in place (no visual jump)
      // Keyframe times must match asset begin_time/end_time so the timeline and Unity stay in sync; if the API
      // omits or overwrites begin/end, force them from the original so the replacement row is found by useAssetPropertiesSync.
      const newAssetForRedux = convertToAssetInstance(newAssetInstanceData);
      const forcedBegin = originalAsset.instanceBeginTime ?? newAssetForRedux.instanceBeginTime;
      const forcedEnd = originalAsset.instanceEndTime ?? newAssetForRedux.instanceEndTime;
      const newAssetForReduxWithTime = {
        ...newAssetForRedux,
        instanceBeginTime: newAssetForRedux.instanceBeginTime ?? forcedBegin,
        instanceEndTime: newAssetForRedux.instanceEndTime ?? forcedEnd,
        isVisible: !hidden,
        isLocked: locked,
      };
      params.dispatch(replaceSceneAsset(params.originalAssetId, newAssetForReduxWithTime));

      // The source still holds state the caller may need to copy across, and it is
      // about to be unrecoverable, so it gets one chance to refuse the removal.
      const gate = (params as { onBeforeSourceRemoval?: (newId: string) => Promise<boolean> }).onBeforeSourceRemoval;
      if (gate) {
        let cleared = false;
        try {
          cleared = await gate(result.newAssetInstanceId as string);
        } catch (err) {
          console.warn('[AssetSwapService] pre-removal gate threw; keeping the original', err);
          cleared = false;
        }
        if (!cleared) {
          const abandonedId = result.newAssetInstanceId as string;
          // The swap's rollback deletes the replacement (and what the gate carried onto it), with the renderer
          // told; the editor shows the original in its place again now
          params.dispatch(replaceSceneAsset(abandonedId, originalAsset));
          params.dispatch(clearKeyframesForAsset(abandonedId));
          params.dispatch(clearClipsForAsset(abandonedId));

          result.success = false;
          result.warnings = [...(result.warnings ?? []), 'source state could not be carried; the swap was abandoned'];
          return result;
        }
      }

      // STEP 4: Delete the old asset with its keyframes and clips, inside the swap's undo step; the asset delete
      // tells the renderer. Only once it is gone do its keyframes and clips leave the editor
      const deleteError = await this.services.asset.deleteAssetInstanceRow(params.originalAssetId, undefined, gesture)
        .then(() => null, (error: unknown) => error);
      if (deleteError) {
        // Still saved and still in the renderer: the editor shows it again, so all three agree
        console.warn('[AssetSwapService] the old asset could not be deleted; it stays in the scene', deleteError);
        const live = params.getState().sceneAssets.sceneAssetsArray as AssetInstance[];
        const at = live.findIndex(a => a.instanceId === newAssetInstanceData.asset_instance_id);
        params.dispatch(addSceneAsset(originalAsset, at < 0 ? undefined : at + 1));
        const reason = deleteError instanceof Error ? deleteError.message : String(deleteError);
        result.warnings.push(`the old asset ${params.originalAssetId} could not be deleted (${reason}); it is still in the scene`);
        const { toast } = await import('sonner');
        toast.error(`The asset was swapped, but the old one could not be removed and is still in the scene: ${reason}`);
      } else {
        params.dispatch(clearKeyframesForAsset(params.originalAssetId));
        params.dispatch(clearClipsForAsset(params.originalAssetId));
      }

      // Clear library cache
      const cacheKey = `${params.sceneId}_defaultAssets`;
      if (globalLibraryCache[cacheKey]) delete globalLibraryCache[cacheKey];

      // STEP 5: Handle start keyframe — addAssetToScene already wrote it to DB, just fetch once
      let startKeyframeForSelection: Keyframe | null = null;

      if (originalStartKeyframe && startPose) {
        const newKfsFromDb = await this.keyframeService.fetchKeyframesForAsset(newAssetInstanceData.asset_instance_id);
        const newStartKfRow = newKfsFromDb.find((kf: SupabaseKeyframe) => kf.keyframe_category === 'start');

        if (newStartKfRow) {
          const { position: pos, rotation: rot, scale: scl } = startPose;
          // Use asset begin_time so start keyframe time is never 0 when the asset actually starts after 0s
          const startKeyframeTime = originalAsset.instanceBeginTime ?? originalStartKeyframe.timePosition;

          const startKf: Keyframe = {
            id:              newStartKfRow.keyframe_id,
            assetInstanceId: newAssetInstanceData.asset_instance_id,
            sceneInstanceId: params.sceneId,
            timePosition:    startKeyframeTime,
            position: pos, rotation: rot, scale: scl,
            category: 'start',
          };

          startKeyframeForSelection = startKf;
          // The renderer places the new start keyframe itself; the original's pose stands over that placement
          poseStated(startKf);
          params.dispatch(upsertKeyframe(startKf));
          result.keyframesCopied++;

          // The whole pose, not a diff against the row read above: the renderer's placement may have been saved over it
          await this.keyframeService.updateKeyframe(
            newStartKfRow.keyframe_id,
            startKeyframeTime,
            pos, rot, scl,
            'start',
            null,
            { scope: gesture },
          );
        }
      }

      // STEP 6: Copy non-start keyframes — all in parallel, no delays
      const nonStartKeyframes = originalKeyframes.filter(kf => kf.category !== 'start');
      if (nonStartKeyframes.length > 0) {
        const kfResults = await Promise.allSettled(
          nonStartKeyframes.map(async kf => {
            const newKfId = Uid();
            const pos = kf.position ?? { x: 0, y: 0, z: 0 };
            const rot = kf.rotation ?? { x: 0, y: 0, z: 0 };
            const scl = kf.scale ?? { x: 1, y: 1, z: 1 };

            const newKf: Keyframe = {
              id: newKfId,
              assetInstanceId: newAssetInstanceData.asset_instance_id,
              sceneInstanceId: params.sceneId,
              timePosition: kf.timePosition,
              position: pos, rotation: rot, scale: scl,
              category: kf.category,
            };

            params.dispatch(upsertKeyframe(newKf));
            await this.keyframeService.createKeyframe(
              newAssetInstanceData.asset_instance_id,
              kf.timePosition, pos, rot, scl,
              kf.category, params.sceneId, newKfId, gesture
            );
          })
        );
        const keyframesFailed = kfResults.filter(r => r.status === 'rejected').length;
        result.keyframesCopied += kfResults.length - keyframesFailed;
        // All or nothing: a keyframe that did not copy fails the swap, which is rolled back
        if (keyframesFailed > 0) {
          result.warnings.push(`${keyframesFailed} keyframe(s) could not be copied`);
          return result;
        }
      }

      // STEP 7: Copy clips to the new instance. Every clip — fades included —
      // must pass a compatibility check against the new asset type, so a clip
      // that doesn't apply to the target is dropped. Fades (Tween-FadeIn/Out)
      // ride the same check via clip_type_scope: e.g. masks:false drops them
      // when swapping to a mask, which can't fade.
      let clipsToCopy: SupabaseClipInstance[] = [];
      if (originalClips.length > 0) {
        const uniqueTypeIds = [...new Set(originalClips.map(c => c.clip_type_id))];
        const clipTypeMap = new Map<string, any>();
        await Promise.allSettled(
          uniqueTypeIds.map(async typeId => {
            try {
              clipTypeMap.set(typeId, await this.clipService.fetchClipTypeRow(typeId));
            } catch { /* unknown → treat as compatible */ }
          })
        );

        clipsToCopy = originalClips.filter(clip => {
          const ct = clipTypeMap.get(clip.clip_type_id);
          if (!ct) return true;
          if (ct.asset_type_id !== null && ct.asset_type_id !== undefined) return ct.asset_type_id === params.newAsset.typeId;
          if (ct.clip_type_scope) {
            const scope = safeParseJson<Record<string, boolean>>(ct.clip_type_scope);
            if (!scope) return true;
            return scope[singularKey as keyof typeof scope] === true ||
                   scope[pluralKey   as keyof typeof scope] === true;
          }
          return true;
        });
      }

      if (clipsToCopy.length > 0) {
        const clipResults = await Promise.allSettled(
          clipsToCopy.map(async clip => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { clip_instance_id: _old, ...rest } = clip as SupabaseClipInstance;
            const newClipRow: SupabaseClipInstance = {
              ...rest,
              clip_instance_id:  Uid(),
              asset_instance_id: newAssetInstanceData.asset_instance_id,
            };
            const inserted = await this.clipService.addClipInstanceRow(newClipRow, gesture);
            params.dispatch(upsertClip(convertToClipInstance(inserted)));
          })
        );
        const clipsFailed = clipResults.filter(r => r.status === 'rejected').length;
        result.clipsCopied = clipResults.length - clipsFailed;
        // All or nothing: a clip that did not copy fails the swap, which is rolled back
        if (clipsFailed > 0) {
          result.warnings.push(`${clipsFailed} clip(s) could not be copied`);
          return result;
        }
      }

      // STEP 8: Select new asset; its start keyframe in the viewport only while it is neither hidden nor locked
      params.dispatch(setSelectedAsset(newAssetInstanceData.asset_instance_id));
      if (startKeyframeForSelection) sendEditorKeyframeSelected(startKeyframeForSelection, this.bridge);

      result.success = true;
      return result;

    } catch (error) {
      console.error('❌ [AssetSwapService] Swap failed:', error);
      result.warnings.push((error as Error).message);
      return result;
    }
  }

  /**
   * Wait for Unity to process the asset and fetch keyframe with retry logic
   */
  private async waitForUnityAndFetchKeyframe(
    assetInstanceId: string,
    category: string,
    maxAttempts: number = 4,
    delayMs: number = 250
  ): Promise<SupabaseKeyframe | null> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
      try {
        const keyframes = await this.keyframeService.fetchKeyframesForAsset(assetInstanceId);
        const kf = keyframes?.find((k: SupabaseKeyframe) => k.keyframe_category === category);
        if (kf) return kf;
      } catch {
        // Continue retrying
      }
      delayMs = Math.min(delayMs * 1.5, 500); // Increase delay up to 500ms
    }
    return null;
  }
}
